import { useCallback, useEffect, useState } from 'react';
import { Loader2, BarChart3 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';
import { aurora } from '@/components/aurora/tokens';
import type { FeedPost, FeedPoll, Workgroup } from '@/pages/messenger/workgroup-hub/types';

interface Props {
  workgroup: Workgroup;
}

/**
 * Tab Polls del gruppo mobile (Task #1721). Filtra i post del feed lato server
 * via post_type=poll, renderizza ogni sondaggio in card compatta e gestisce
 * voto/revoca riusando gli stessi endpoint del desktop ChatTab.
 */
export default function MobileWorkgroupPolls({ workgroup }: Props) {
  const [polls, setPolls] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<FeedPost[]>(
        `/messenger/feed/posts?target_type=workgroup&target_id=${workgroup.id}&post_type=poll&limit=30`,
      );
      const list = Array.isArray(res.data) ? res.data : [];
      setPolls(list.filter((p) => !!p.poll));
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => { load(); }, [load]);

  async function vote(poll: FeedPoll, optionId: string) {
    const already = poll.user_votes.includes(optionId);
    try {
      if (already) {
        await apiClient.delete(`/messenger/feed/polls/${poll.id}/vote?option_id=${encodeURIComponent(optionId)}`);
      } else {
        await apiClient.post(`/messenger/feed/polls/${poll.id}/vote`, { option_id: optionId });
      }
      load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore';
      alert(`Errore voto: ${msg}`);
    }
  }

  async function revoke(poll: FeedPoll) {
    try {
      await apiClient.delete(`/messenger/feed/polls/${poll.id}/vote`);
      load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore';
      alert(`Errore revoca voto: ${msg}`);
    }
  }

  if (loading) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }

  if (polls.length === 0) {
    return (
      <div className="px-4 py-12 text-center text-muted-foreground/80">
        <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-60" />
        <p className="text-sm">Nessun sondaggio in questo gruppo.</p>
        <p className="text-xs mt-1">I sondaggi creati nel feed appariranno qui (creazione disponibile da desktop).</p>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 space-y-3">
      {polls.map((post) => {
        const poll = post.poll;
        if (!poll) return null;
        const closed = poll.closes_at ? new Date(poll.closes_at) < new Date() : false;
        const userHasVoted = poll.user_votes.length > 0;
        const total = Math.max(poll.total_votes, 1);
        return (
          <article key={post.id} className="bg-card border border-border rounded-2xl p-3">
            <header className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <h3 className={cn(aurora.type.bodyStrong, 'text-foreground')}>{poll.question}</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {post.author_first_name} {post.author_last_name} ·{' '}
                  {new Date(post.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                </p>
              </div>
              {closed && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 shrink-0">
                  Chiuso
                </span>
              )}
            </header>
            <div className="space-y-1.5">
              {poll.options.map((opt) => {
                const pct = poll.total_votes > 0 ? Math.round((opt.vote_count / total) * 100) : 0;
                const voted = poll.user_votes.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={closed}
                    onClick={() => vote(poll, opt.id)}
                    className={cn(
                      'relative w-full text-left rounded-lg border overflow-hidden disabled:opacity-60 min-h-[40px]',
                      voted ? 'border-blue-500/60' : 'border-border',
                    )}
                    aria-pressed={voted}
                  >
                    <div
                      className={cn('absolute inset-y-0 left-0', voted ? 'bg-blue-500/25' : 'bg-muted')}
                      style={{ width: `${pct}%` }}
                    />
                    <div className="relative flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-foreground truncate">{opt.option_text}</span>
                      <span className="text-xs text-muted-foreground ml-2 shrink-0">
                        {opt.vote_count} · {pct}%
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <footer className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>
                {poll.total_votes} voti
                {poll.is_anonymous ? ' · anonimo' : ''}
                {poll.is_multiple_choice ? ' · scelta multipla' : ''}
              </span>
              {userHasVoted && !closed && (
                <button onClick={() => revoke(poll)} className="text-blue-400 min-h-[32px]">
                  Revoca voto
                </button>
              )}
            </footer>
          </article>
        );
      })}
    </div>
  );
}
